import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowRight,
  Check,
  ExternalLink,
  FileText,
  ShieldCheck,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import type { ProviderId } from '@/src/infrastructure/config/firebase';
import type { AuthResult } from '@/src/modules/auth/session';
import { BrandLogo } from '@/src/modules/components/BrandLogo';
import { AnimatedGradientBackground } from '@/src/modules/components/background/AnimatedGradientBackground';
import { CURRENT_POLICY_VERSION } from '@/src/infrastructure/config/policyVersion';

type Tab = 'login' | 'signup';

interface LoginProps {
  /** Retained for route compatibility; social login has no separate sign-up. */
  initialTab?: Tab;
}

const authHighlights: Array<[string, LucideIcon]> = [
  ['Secure sign-in', ShieldCheck],
  ['Fast publish', ArrowRight],
  ['Premium templates', Sparkles],
];

// ── Provider icons ───────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.8 15.71 17.58V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z" fill="#4285F4" />
      <path d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.58C14.73 18.24 13.48 18.64 12 18.64C9.13 18.64 6.7 16.7 5.84 14.11H2.17V16.96C3.99 20.58 7.7 23 12 23Z" fill="#34A853" />
      <path d="M5.84 14.11C5.62 13.45 5.5 12.74 5.5 12C5.5 11.26 5.62 10.55 5.84 9.89V7.04H2.17C1.42 8.53 1 10.22 1 12C1 13.78 1.42 15.47 2.17 16.96L5.84 14.11Z" fill="#FBBC05" />
      <path d="M12 5.38C13.62 5.38 15.06 5.93 16.21 7.02L19.35 3.88C17.45 2.1 14.97 1 12 1C7.7 1 3.99 3.42 2.17 7.04L5.84 9.89C6.7 7.3 9.13 5.38 12 5.38Z" fill="#EA4335" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07Z"
        fill="#1877F2"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 23 23" width="20" height="20" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H1z" />
    </svg>
  );
}

// ── Provider button ──────────────────────────────────────────────────────────

function ProviderButton({
  icon,
  label,
  loading,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-2xl border border-outline-variant/60 bg-white px-5 text-sm font-bold text-on-surface shadow-sm transition-all hover:-translate-y-0.5 hover:bg-surface-lowest disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
    >
      {loading ? (
        <span className="h-5 w-5 rounded-full border-2 border-on-surface-variant border-t-transparent animate-spin" />
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

export function Login({ initialTab }: LoginProps) {
  const navigate = useNavigate();
  const { user, hydrated, loginWithGoogle, loginWithFacebook, loginWithMicrosoft } =
    useAuth();

  const [pending, setPending] = useState<ProviderId | null>(null);
  const [error, setError] = useState('');
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<{
    id: ProviderId;
    fn: () => Promise<AuthResult>;
  } | null>(null);

  // Once auth state resolves to a signed-in user, leave the login page.
  useEffect(() => {
    if (hydrated && user) navigate('/dashboard', { replace: true });
  }, [hydrated, user, navigate]);

  const isSignup = initialTab === 'signup';
  const copy = useMemo(
    () =>
      isSignup
        ? {
            eyebrow: 'Start designing',
            title: 'Create your InviteCloud account',
            body: 'Launch polished invitation websites with templates, media, RSVPs, and publishing built in.',
          }
        : {
            eyebrow: 'Welcome back',
            title: 'Sign in to your invitation studio',
            body: 'Create, publish, and manage beautiful guest experiences from one secure dashboard.',
          },
    [isSignup],
  );

  const recordPolicyAcceptance = (method: 'checkbox' | 'modal_confirmation') => {
    const record = {
      accepted: true,
      acceptedAt: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
      acceptedVia: method,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };
    try {
      localStorage.setItem('invitecloud.policy_acceptance.v1', JSON.stringify(record));
    } catch (_) {}
    setPolicyAccepted(true);
    import('@/src/infrastructure/lib/analytics').then(({ trackEvent }) => {
      trackEvent('POLICY_ACCEPTED', 'compliance', record.policyVersion);
    });
  };

  const handle = async (id: ProviderId, fn: () => Promise<AuthResult>) => {
    setError('');
    setPending(id);
    const result = await fn();
    // On success, record policy audit log and check attribution
    if (result.ok) {
      try {
        const acceptanceRecord = localStorage.getItem('invitecloud.policy_acceptance.v1');
        if (acceptanceRecord) {
          const parsed = JSON.parse(acceptanceRecord);
          import('@/src/infrastructure/lib/analytics').then(({ trackEvent }) => {
            trackEvent('POLICY_AGREEMENT_RECORDED', 'auth_audit', `${id}:${parsed.acceptedAt}`);
          });
        }

        const attrRaw = localStorage.getItem('invitecloud.attribution.v1');
        if (attrRaw) {
          const attr = JSON.parse(attrRaw);
          const time = new Date(attr.timestamp).getTime();
          if (Date.now() - time < 7 * 24 * 60 * 60 * 1000 && attr.referredByInvitationSlug) {
            import('@/src/infrastructure/lib/analytics').then(({ trackEvent }) => {
              trackEvent('RSVP_CONVERTED_TO_USER', 'viral_conversion', attr.referredByInvitationSlug);
            });
            localStorage.removeItem('invitecloud.attribution.v1');
          }
        }
      } catch (_) {}
      return;
    }
    setError(result.error ?? 'Sign-in failed. Please try again.');
    setPending(null);
  };

  const handleProviderClick = (id: ProviderId, fn: () => Promise<AuthResult>) => {
    if (!policyAccepted) {
      setPendingProvider({ id, fn });
      setShowPolicyModal(true);
      return;
    }
    handle(id, fn);
  };

  const handleModalAccept = () => {
    recordPolicyAcceptance('modal_confirmation');
    setShowPolicyModal(false);
    if (pendingProvider) {
      handle(pendingProvider.id, pendingProvider.fn);
      setPendingProvider(null);
    }
  };

  const busy = pending !== null;

  return (
    <div className="force-light relative min-h-screen overflow-hidden bg-surface text-on-surface">
      <AnimatedGradientBackground />
      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* ── Left hero panel ───────────────────────────────────────────── */}
        <section className="relative hidden overflow-hidden border-r border-white/45 px-12 py-10 lg:flex lg:flex-col lg:justify-between">
          <div className="relative z-10">
            <BrandLogo variant="full" size="xl" href="/" />
          </div>

          <div className="relative z-10 max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28 }}
              className="space-y-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white/65 px-4 py-2 text-sm font-bold text-brand-deep shadow-sm backdrop-blur">
                <Sparkles className="h-4 w-4" />
                {copy.eyebrow}
              </span>
              <div className="space-y-4">
                <h1 className="max-w-[12ch] text-6xl font-bold leading-[0.95] tracking-normal text-on-surface">
                  {copy.title}
                </h1>
                <p className="max-w-md text-lg font-medium leading-8 text-on-surface-variant">
                  {copy.body}
                </p>
              </div>
            </motion.div>
          </div>

          <div className="relative z-10 grid grid-cols-3 gap-3">
            {authHighlights.map(([label, Icon]) => (
              <div key={label} className="rounded-2xl border border-white/70 bg-white/60 p-4 shadow-sm backdrop-blur">
                <Icon className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-bold text-on-surface">{label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Auth card ─────────────────────────────────────────────────── */}
        <main className="flex items-center justify-center px-5 py-10 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="mb-8 flex justify-center lg:hidden">
              <BrandLogo variant="full" size="lg" href="/" />
            </div>

            <div className="rounded-[2rem] border border-white/70 bg-white/80 p-6 shadow-2xl shadow-brand-deep/10 backdrop-blur sm:p-8">
              <div className="mb-7 space-y-2 text-center">
                <p className="text-sm font-bold uppercase tracking-wider text-primary">
                  {copy.eyebrow}
                </p>
                <h2 className="text-3xl font-bold tracking-normal text-on-surface">
                  Continue to InviteCloud
                </h2>
                <p className="text-sm font-medium leading-6 text-on-surface-variant">
                  Sign in with a social account — no passwords to remember.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <ProviderButton
                  icon={<GoogleIcon />}
                  label="Continue with Google"
                  loading={pending === 'google'}
                  disabled={busy}
                  onClick={() => handleProviderClick('google', loginWithGoogle)}
                />
                <ProviderButton
                  icon={<FacebookIcon />}
                  label="Continue with Facebook"
                  loading={pending === 'facebook'}
                  disabled={busy}
                  onClick={() => handleProviderClick('facebook', loginWithFacebook)}
                />
                <ProviderButton
                  icon={<MicrosoftIcon />}
                  label="Continue with Microsoft"
                  loading={pending === 'microsoft'}
                  disabled={busy}
                  onClick={() => handleProviderClick('microsoft', loginWithMicrosoft)}
                />
              </div>

              {/* ── Explicit Policy Agreement Checkbox ──────────────────── */}
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-outline-variant/40 bg-white/60 p-3.5 shadow-sm">
                <input
                  id="policy-agreement-checkbox"
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={(e) => {
                    if (e.target.checked) {
                      recordPolicyAcceptance('checkbox');
                    } else {
                      setPolicyAccepted(false);
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer accent-primary"
                />
                <label
                  htmlFor="policy-agreement-checkbox"
                  className="text-xs font-medium leading-5 text-on-surface-variant cursor-pointer select-none"
                >
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowPolicyModal(true)}
                    className="font-bold text-primary underline hover:text-primary/80 transition-colors"
                  >
                    Terms of Service, Privacy Policy & User Agreement
                  </button>.
                </label>
              </div>

              {error && (
                <p
                  role="alert"
                  className="mt-5 rounded-2xl border border-status-failed/20 bg-status-failed/10 px-4 py-3 text-sm font-semibold text-status-failed"
                >
                  {error}
                </p>
              )}

              <p className="mt-6 text-center text-xs font-medium leading-5 text-on-surface-variant">
                By continuing you agree to the InviteCloud Terms of Service,
                Privacy Policy, and Acceptable Use Policy.
              </p>
            </div>
          </motion.div>
        </main>
      </div>

      {/* ── Policy Agreement Modal / Pop-up ───────────────────────────── */}
      <AnimatePresence>
        {showPolicyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPolicyModal(false)}
              className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative z-10 w-full max-w-2xl rounded-3xl border border-white/80 bg-white p-6 shadow-2xl sm:p-8"
            >
              <div className="flex items-center justify-between border-b border-outline-variant/20 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-on-surface">
                      InviteCloud Terms & User Agreement
                    </h3>
                    <p className="text-xs text-on-surface-variant">
                      Please review and accept our platform policies to continue
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPolicyModal(false)}
                  className="rounded-full p-2 text-on-surface-variant hover:bg-surface-lowest transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="my-5 max-h-[60vh] overflow-y-auto space-y-5 pr-2 text-xs text-on-surface-variant leading-relaxed">
                {pendingProvider && !policyAccepted && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-3.5 text-xs font-semibold text-primary">
                    Please accept the User Agreement below to proceed with sign-in.
                  </div>
                )}

                <div className="rounded-xl border border-outline-variant/30 bg-surface-lowest p-4">
                  <h4 className="font-bold text-on-surface text-sm mb-1">
                    1. Terms & Conditions & Jurisdiction
                  </h4>
                  <p>
                    The InviteCloud platform is operated by InviteCloud. By creating an account or logging in, you agree to our Terms of Service governed under Indian jurisdiction.
                  </p>
                </div>

                <div className="rounded-xl border border-outline-variant/30 bg-surface-lowest p-4">
                  <h4 className="font-bold text-on-surface text-sm mb-1">
                    2. Privacy, DPDP Act & Telemetry
                  </h4>
                  <p>
                    We collect minimal personal data under the DPDP Act. Analytics (GA4) tracks aggregated pageviews and template selections with <strong>zero PII captured</strong>. Contact us at <a href="mailto:support@invitecloud.in" className="text-primary underline">support@invitecloud.in</a> or <a href="mailto:founder@invitecloud.in" className="text-primary underline">founder@invitecloud.in</a>.
                  </p>
                </div>

                <div className="rounded-xl border border-outline-variant/30 bg-surface-lowest p-4">
                  <h4 className="font-bold text-on-surface text-sm mb-1">
                    3. Acceptable Use & AI Safety
                  </h4>
                  <p>
                    All uploaded content must follow our Acceptable Use Policy. Automated AI safety models inspect content before publishing. AI processing utilizes Google Gemini API, Cloudflare AI, and Remotion pipelines.
                  </p>
                </div>

                <div className="rounded-xl border border-outline-variant/30 bg-surface-lowest p-4">
                  <h4 className="font-bold text-on-surface text-sm mb-1">
                    4. Payments, Credits & Dispute Resolution
                  </h4>
                  <p>
                    Paid subscriptions and per-template credits are processed via Razorpay/Stripe with GST compliance. Failed transactions generate unique reference IDs (<code>TXN-...</code> / <code>pay_...</code>) for prompt support resolution.
                  </p>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                  >
                    View Full Policy Hub (/terms) <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 border-t border-outline-variant/20 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPolicyModal(false)}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-outline-variant text-xs font-bold text-on-surface hover:bg-surface-lowest transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleModalAccept}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-on-primary text-xs font-bold shadow-md hover:bg-primary-dark transition-all"
                >
                  <Check className="h-4 w-4" />
                  I Agree & Continue
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Login;


